import React, { useState, useEffect, useContext } from 'react'
import ApiService from '../../../../services/ApiService'
import {
  Container,
  HeaderDiv,
  HeaderText,
  CloseDiv,
  CloseImg,
  ProgressBarDiv,
  ProgressBarCircle,
  ProgressBarLine,
  InputRowDiv,
  InputDiv,
  InputLabel,
  Input,
  SelectInput,
  SelectOption,
  ButtonsDiv,
  PreviousButtonDiv,
  PreviousButtonImage,
  PreviousButtonText,
  NextButtonDiv,
  NextButtonImage,
  NextButtonText,
  WarningDiv,
  WarningText,
} from './style'
import ModalContext from '../../../../contexts/ModalContext'
import StockContext from '../../../../contexts/StockContext'
import UserContext from '../../../../contexts/UserContext'
import cross from '../../../../assets/addStockModal/cross.svg'
import arrowRight from '../../../../assets/addStockModal/pages/arrow-right.svg'
import arrowLeft from '../../../../assets/addStockModal/pages/arrow-left.svg'
import {
  FieldsToHeaders,
  fieldsToHeaders,
  StockInterface,
} from '../../../../pages/ClientPanel/Stock/StockTable/utils'
import { Info } from 'lucide-react'
import 'font-awesome/css/font-awesome.min.css'

const SecondCustomAddPage: React.FC = () => {
  const { stock, setStock } = useContext(StockContext)
  const { setModalActive, setModalSection, itIsUpdate } =
    useContext(ModalContext)
  const { token, user } = useContext(UserContext)

  const [activeMenu, setActiveMenu] = useState(0)
  const [warningMessage, setWarningMessage] = useState('')
  const [isNextDisabled, setIsNextDisabled] = useState(true)
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({})

  // Sempre limpa aviso ao mudar stock
  useEffect(() => {
    setWarningMessage('')
  }, [stock])

  // Validação dos campos obrigatórios
  useEffect(() => {
    console.log('🔍 [SECONDPAGE] Revalidando campos obrigatórios...', {
      measurementUnit: stock.measurementUnit,
      dimension1: stock.dimension1,
      totalWeight: stock.totalWeight,
      costPerKg: stock.costPerKg,
      lengthInMm: stock.lengthInMm,
      costPerMm: stock.costPerMm
    });
    
    const requiredFields = ['dimension1']

    if (stock.measurementUnit === 'KG') {
      requiredFields.push('totalWeight', 'costPerKg')
      console.log('💰 [SECONDPAGE] Modo KG - Campos obrigatórios:', requiredFields);
    } else if (stock.measurementUnit === 'MM') {
      requiredFields.push('lengthInMm', 'costPerMm')
      console.log('📏 [SECONDPAGE] Modo MM - Campos obrigatórios:', requiredFields);
    }

    const allFieldsFilled = requiredFields.every(
      (field) => stock[field as keyof StockInterface],
    )
    
    console.log('✅ [SECONDPAGE] Resultado da validação:', {
      requiredFields,
      allFieldsFilled,
      missingFields: requiredFields.filter(field => !stock[field as keyof StockInterface]),
      isNextDisabled: !allFieldsFilled
    });

    setIsNextDisabled(!allFieldsFilled)

    const newErrors: { [key: string]: boolean } = {}
    requiredFields.forEach((field) => {
      newErrors[field] = !stock[field as keyof StockInterface]
    })

    setErrors(newErrors)
  }, [stock])

  // Input comum
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    
    console.log('📝 [SECONDPAGE] Campo alterado:', {
      field: name,
      oldValue: stock[name as keyof StockInterface],
      newValue: value,
      isNumber: !isNaN(Number(value))
    });
    
    if (isNaN(Number(value))) {
      console.warn('⚠️ [SECONDPAGE] Valor inválido (não é número):', value);
      setWarningMessage('Digite apenas números válidos!')
      return
    }
    
    setStock({ ...stock, [name]: value })
    
    console.log('✅ [SECONDPAGE] Stock atualizado com sucesso:', {
      field: name,
      newValue: value,
      updatedStock: { ...stock, [name]: value }
    });
  }

  // Select (unidade)
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target
    
    console.log('🔍 [SECONDPAGE] Select alterado:', {
      field: name,
      oldValue: stock[name as keyof StockInterface],
      newValue: value,
      affectedValidation: name === 'measurementUnit' ? 'Altera campos obrigatórios' : 'Não afeta validação'
    });
    
    setStock({ ...stock, [name]: value })
    
    console.log('✅ [SECONDPAGE] Unidade de medida atualizada:', {
      newUnit: value,
      willRequire: value === 'KG' ? ['totalWeight', 'costPerKg'] : value === 'MM' ? ['lengthInMm', 'costPerMm'] : []
    });
  }

  // Navegação
  const previousPage = () => {
    console.log('🔙 [SECONDPAGE] Voltando para primeira página');
    setModalSection('firstCustomAdd')
  }
  
  const nextPage = () => {
    console.log('🚀 [SECONDPAGE] Iniciando navegação para terceira página');
    console.log('📋 [SECONDPAGE] Estado atual do stock:', {
      quantity: stock.quantity,
      totalWeight: stock.totalWeight,
      totalCmc: stock.totalCmc,
      measurementUnit: stock.measurementUnit,
      dimension1: stock.dimension1,
      dimension2: stock.dimension2,
      lengthInMm: stock.lengthInMm,
      costPerKg: stock.costPerKg,
      costPerMm: stock.costPerMm,
      isNextDisabled
    });
    
    console.log('🔍 [SECONDPAGE] Validação de campos obrigatórios:', {
      measurementUnit: stock.measurementUnit,
      requiredForKG: stock.measurementUnit === 'KG' ? ['totalWeight', 'costPerKg'] : [],
      requiredForMM: stock.measurementUnit === 'MM' ? ['lengthInMm', 'costPerMm'] : [],
      dimension1: stock.dimension1,
      canProceed: !isNextDisabled
    });
    
    if (!isNextDisabled) {
      console.log('✅ [SECONDPAGE] Validação passou, navegando para terceira página');
      console.log('💾 [SECONDPAGE] IMPORTANTE: Apenas atualizando contexto, NÃO salvando no banco ainda');
      console.log('🔄 [SECONDPAGE] Chamando setModalSection("thirdCustomAdd")');
      setModalSection('thirdCustomAdd')
    } else {
      console.error('❌ [SECONDPAGE] Validação falhou, permanecendo na segunda página');
    }
  }

  // ----------------------------
  // Função de cálculo
  // ----------------------------
  const handleCalculate = (field: 'totalWeight' | 'lengthInMm') => {
    const { format, density, dimension1, dimension2, lengthInMm, totalWeight } =
      stock

    const fmt = (format || '').toUpperCase().trim()
    const dens = Number(density)
    const d1 = Number(dimension1)
    const d2 = Number(dimension2) || 0
    const length = Number(lengthInMm)
    const weight = Number(totalWeight)

    if (!fmt || !dens || !d1) {
      setWarningMessage('Preencha formato, densidade e dimensão 1!')
      return
    }

    let area = 0
    switch (fmt) {
      case 'BARRA':
      case 'REDONDO':
        area = Math.PI * (d1 / 2) ** 2
        break
      case 'QUADRADO':
      case 'CHAPA':
        if (!d2) {
          setWarningMessage('Informe a dimensão 2!')
          return
        }
        area = d1 * d2
        break
      case 'TUBO':
        if (!d2) {
          setWarningMessage('Para TUBO precisa das duas dimensões!')
          return
        }
        area = Math.PI * ((d1 / 2) ** 2 - (d2 / 2) ** 2)
        break
      case 'LINGOTE':
        if (!d2) {
          setWarningMessage('Informe a dimensão 2!')
          return
        }
        area = d1 * d2
        break
      default:
        setWarningMessage('Formato inválido!')
        return
    }

    if (field === 'totalWeight') {
      if (!length) {
        setWarningMessage('Informe o comprimento!')
        return
      }
      const newWeight = (area * length * dens) / 1_000_000
      setStock({ ...stock, totalWeight: String(newWeight.toFixed(2)) })
      setWarningMessage('')
    } else if (field === 'lengthInMm') {
      if (!weight) {
        setWarningMessage('Informe o peso!')
        return
      }
      const newLength = (weight * 1_000_000) / (area * dens)
      setStock({ ...stock, lengthInMm: String(newLength.toFixed(2)) })
      setWarningMessage('')
    }
  }



  // Array de inputs por linha
  const fieldsArray: string[][] = [
    ['quantity', 'totalWeight', 'totalCmc'],
    ['measurementUnit', 'dimension1', 'dimension2'],
    ['lengthInMm', 'costPerKg', 'costPerMm'],
  ]

  return (
    <Container>
      <HeaderDiv>
        <HeaderText>
          {itIsUpdate ? 'Editar Estoque' : 'Adicionar Estoque'}
        </HeaderText>
        <CloseDiv onClick={() => setModalActive(false)}>
          <CloseImg src={cross} alt='Close tab' />
        </CloseDiv>
      </HeaderDiv>

      <ProgressBarDiv>
        <ProgressBarCircle className='active' />
        <ProgressBarLine className='active' />
        <ProgressBarCircle className='active' />
        <ProgressBarLine />
        <ProgressBarCircle />
      </ProgressBarDiv>

      {fieldsArray.map((inputRow, rowIndex) => (
        <InputRowDiv key={rowIndex}>
          {inputRow.map((stockField: string, colIndex) => (
            <InputDiv key={colIndex} style={{ position: 'relative' }}>
              <InputLabel htmlFor={stockField}>
                {fieldsToHeaders[stockField as keyof FieldsToHeaders]}
                {errors[stockField] !== undefined && (
                  <Info
                    size={15}
                    style={{ marginLeft: 5 }}
                    color={
                      !stock[stockField as keyof StockInterface]
                        ? 'red'
                        : 'gray'
                    }
                  />
                )}
              </InputLabel>

              {stockField !== 'measurementUnit' ? (
                <>
                  <Input
                    placeholder='Digite aqui'
                    name={stockField}
                    value={stock[stockField as keyof StockInterface]}
                    onFocus={() => setActiveMenu(rowIndex * 3 + colIndex + 1)}
                    onBlur={() => setActiveMenu(0)}
                    onChange={handleChange}
                    autoComplete='off'
                  />
                  {(stockField === 'totalWeight' ||
                    stockField === 'lengthInMm') && (
                    <i
                      className='fa fa-calculator'
                      style={{
                        position: 'absolute',
                        right: 10,
                        top: 35,
                        cursor: 'pointer',
                        color: '#007bff',
                      }}
                      onClick={() =>
                        handleCalculate(
                          stockField as 'totalWeight' | 'lengthInMm',
                        )
                      }
                    />
                  )}
                </>
              ) : (
                <SelectInput
                  name={stockField}
                  value={stock[stockField as keyof StockInterface]}
                  onChange={handleSelectChange}
                >
                  <SelectOption value='KG'>KG</SelectOption>
                  <SelectOption value='MM'>MM</SelectOption>
                </SelectInput>
              )}
            </InputDiv>
          ))}
        </InputRowDiv>
      ))}

      <ButtonsDiv>
        <WarningDiv>
          <WarningText>{warningMessage}</WarningText>
        </WarningDiv>

        <PreviousButtonDiv onClick={previousPage}>
          <PreviousButtonImage src={arrowLeft} alt='Right arrow icon' />
          <PreviousButtonText>Voltar</PreviousButtonText>
        </PreviousButtonDiv>

        <NextButtonDiv
          onClick={nextPage}
          style={{
            opacity: isNextDisabled ? 0.5 : 1,
            pointerEvents: isNextDisabled ? 'none' : 'auto',
          }}
        >
          <NextButtonImage src={arrowRight} alt='Right arrow icon' />
          <NextButtonText>Próximo</NextButtonText>
        </NextButtonDiv>
      </ButtonsDiv>
    </Container>
  )
}

export default SecondCustomAddPage
